import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, of, shareReplay, tap } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import type { AuthResponse, User } from '../models/user.model';
import { UsersService } from './users.service';

export interface SignupPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  promoCode?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly usersService = inject(UsersService);

  /** Held only in memory — never localStorage — to limit XSS blast radius. */
  private accessToken: string | null = null;

  readonly currentUser = signal<User | null>(null);
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  /** Flips true once the initial silent-refresh bootstrap attempt resolves,
   * so the header/guards don't flash a logged-out state on page load. */
  readonly authReady = signal(false);

  constructor(private readonly http: HttpClient) {}

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private bootstrap$: Observable<AuthResponse | null> | null = null;

  /** Attempts to restore a session from the httpOnly refresh cookie. Always
   * resolves — failure just leaves the user logged out. Idempotent: the
   * first caller kicks off the request, later callers share it. */
  bootstrap(): Observable<AuthResponse | null> {
    if (!this.bootstrap$) {
      this.bootstrap$ = this.refreshAccessToken().pipe(
        tap(() => this.authReady.set(true)),
        catchError(() => {
          this.authReady.set(true);
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.bootstrap$;
  }

  signup(payload: SignupPayload): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_BASE_URL}/auth/signup`, payload, {
        withCredentials: true,
      })
      .pipe(tap((res) => this.setSession(res)));
  }

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_BASE_URL}/auth/login`, payload, {
        withCredentials: true,
      })
      .pipe(tap((res) => this.setSession(res)));
  }

  requestPasswordReset(email: string): Observable<unknown> {
    return this.http.post(`${API_BASE_URL}/auth/forgot-password`, { email });
  }

  verifyEmail(email: string, code: string): Observable<unknown> {
    return this.http.post(`${API_BASE_URL}/auth/verify-email`, { email, code });
  }

  resendVerificationCode(email: string): Observable<unknown> {
    return this.http.post(`${API_BASE_URL}/auth/resend-verification`, { email });
  }

  resetPassword(token: string, password: string): Observable<unknown> {
    return this.http.post(`${API_BASE_URL}/auth/reset-password`, {
      token,
      password,
    });
  }

  logout(): Observable<unknown> {
    return this.http
      .post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true })
      .pipe(tap(() => this.clearSession()));
  }

  /** Exchanges the httpOnly refresh cookie for a new access token. Used both
   * at startup and by the HTTP interceptor when a request 401s. A missing
   * cookie returns 204 (no body) rather than 401 — treat that as logged out. */
  refreshAccessToken(): Observable<AuthResponse | null> {
    return this.http
      .post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((res) => {
          if (res?.accessToken) this.setSession(res);
        }),
      );
  }

  private setSession(res: AuthResponse): void {
    // A routine silent refresh re-sets the same identity every ~15 minutes
    // (access token TTL) — only reset the cached /users/me profile when the
    // identity actually changes (login/signup as someone new after a prior
    // session), not on every token rotation.
    const previousUserId = this.currentUser()?.id;
    this.accessToken = res.accessToken;
    this.currentUser.set(res.user);
    if (previousUserId && previousUserId !== res.user.id) {
      this.usersService.resetMe();
    }
  }

  /** Public so the auth interceptor can clear state when a refresh attempt
   * fails after a 401 (e.g. the refresh token was revoked or expired). */
  clearSession(): void {
    this.accessToken = null;
    this.currentUser.set(null);
    // Whoever logs in next in this tab must never see the previous
    // account's cached profile (KYC/verification status, rating, etc.).
    this.usersService.resetMe();
  }
}
